import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_IMAGE,
  SITE_NAME,
  SITE_URL,
  getSeoForPath,
  normalizeCanonicalPath,
  titleFromSlug,
} from "../seo/routeSeo";

function ensureMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  return element;
}

function ensureLink(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("link");
    Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
    document.head.appendChild(element);
  }
  return element;
}

function ensureJsonLd() {
  let element = document.head.querySelector('script[data-seo-jsonld="primary"]');
  if (!element) {
    element = document.createElement("script");
    element.setAttribute("type", "application/ld+json");
    element.setAttribute("data-seo-jsonld", "primary");
    document.head.appendChild(element);
  }
  return element;
}

function buildBreadcrumbs(pathname) {
  const parts = pathname.split("/").filter(Boolean);
  const items = [
    {
      "@type": "ListItem",
      position: 1,
      name: "Home",
      item: `${SITE_URL}/`,
    },
  ];

  let currentPath = "";
  parts.forEach((part, index) => {
    currentPath += `/${part}`;
    items.push({
      "@type": "ListItem",
      position: index + 2,
      name: titleFromSlug(part),
      item: `${SITE_URL}${currentPath}`,
    });
  });

  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items,
  };
}

function buildStructuredData({ title, description, canonical, pathname, type }) {
  const website = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE_URL}/search?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };

  const page = {
    "@context": "https://schema.org",
    "@type": type || "WebPage",
    name: title,
    description: description || DEFAULT_DESCRIPTION,
    url: canonical,
    isPartOf: {
      "@type": "WebSite",
      name: SITE_NAME,
      url: `${SITE_URL}/`,
    },
    primaryImageOfPage: DEFAULT_IMAGE,
  };

  const organization = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    logo: DEFAULT_IMAGE,
  };

  const structuredData = [website, organization, page];

  if (pathname !== "/") {
    structuredData.push(buildBreadcrumbs(pathname));
  }

  return structuredData;
}

function Seo() {
  const location = useLocation();

  useEffect(() => {
    const pathname = normalizeCanonicalPath(location.pathname || "/");
    const seo = getSeoForPath(pathname);
    const isDetailPage = /(materials|pets|hangouts|profiles)\/[^/]+$/.test(pathname);
    const detailLabel = isDetailPage ? titleFromSlug(pathname) : null;
    const title = isDetailPage
      ? `${detailLabel} | ${SITE_NAME}`
      : seo.title;
    const description = isDetailPage
      ? `View ${detailLabel} on ${SITE_NAME} and explore trusted local exchange details and community connections.`
      : seo.description;
    const canonical = seo.canonical || `${SITE_URL}${pathname === "/" ? "/" : pathname}`;
    const robots = seo.robots || "index, follow";
    const structuredData = buildStructuredData({
      title,
      description,
      canonical,
      pathname,
      type: seo.type,
    });

    document.title = title;
    ensureMeta('meta[name="description"]', { name: "description" }).setAttribute("content", description);
    ensureMeta('meta[name="robots"]', { name: "robots" }).setAttribute("content", robots);
    ensureMeta('meta[property="og:title"]', { property: "og:title" }).setAttribute("content", title);
    ensureMeta('meta[property="og:description"]', { property: "og:description" }).setAttribute("content", description);
    ensureMeta('meta[property="og:type"]', { property: "og:type" }).setAttribute("content", "website");
    ensureMeta('meta[property="og:url"]', { property: "og:url" }).setAttribute("content", canonical);
    ensureMeta('meta[property="og:site_name"]', { property: "og:site_name" }).setAttribute("content", SITE_NAME);
    ensureMeta('meta[property="og:image"]', { property: "og:image" }).setAttribute("content", DEFAULT_IMAGE);
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card" }).setAttribute("content", "summary_large_image");
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title" }).setAttribute("content", title);
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description" }).setAttribute("content", description);
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image" }).setAttribute("content", DEFAULT_IMAGE);
    ensureLink('link[rel="canonical"]', { rel: "canonical" }).setAttribute("href", canonical);
    ensureJsonLd().textContent = JSON.stringify(structuredData);
  }, [location.pathname]);

  return null;
}

export default Seo;
